// askSmart-ui/src/pages/SMRTCOPTemplateEditor.tsx
import React, { useEffect, useRef, useState } from "react";

// ─── Types ───────────────────────────────────────────────────────────────────
// Defined inline — SMRT COP is a fully independent module.

type COPQuestionType =
  | "text"
  | "textarea"
  | "number"
  | "select"
  | "checkbox"
  | "date"
  | "photo"
  | "heading"
  | "certification";

interface COPVisibilityRule {
  questionKey: string;
  operator: "equals" | "notEquals";
  value: string;
}

interface COPQuestion {
  key: string;
  label: string;
  helper?: string;
  type: COPQuestionType;
  required: boolean;
  options?: string[];
  minLength?: number;
  maxLength?: number;
  mask?: string;
  visibleWhen?: COPVisibilityRule[];
}

type COPSectionType = "manual" | "bom" | "photo" | "reports" | "alignment" | "jha";

interface COPSection {
  id: number;
  label: string;
  questions: COPQuestion[];
  // Legacy templates have no type; treat any missing value as "manual".
  type?: COPSectionType;
}

interface COPTemplate {
  id: string;
  name: string;
  version: number;
  status: "draft" | "active";
  sections: COPSection[];
  createdAt: string;
  updatedAt: string;
}

// ─── Props ───────────────────────────────────────────────────────────────────

type Props = {
  template: COPTemplate;
  editingSectionId: number | null;
  dirty: boolean;
  readOnly?: boolean;
  mode?: "view" | "run";

  onStartEdit: (sectionId: number) => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onUpdateSectionLabel: (sectionId: number, label: string) => void;
  onMoveSection: (sectionId: number, direction: "up" | "down") => void;
  onDeleteSection: (sectionId: number) => void;
  onAddSection: (type: COPSectionType) => void;

  onUpdateQuestion: (
    sectionId: number,
    questionKey: string,
    updates: Partial<COPQuestion>,
  ) => void;
  onReorderQuestion: (
    sectionId: number,
    fromKey: string,
    toKey: string,
    after: boolean,
  ) => void;
  onAddQuestion: (sectionId: number) => void;
  onDeleteQuestion: (sectionId: number, questionKey: string) => void;
};

// ─── Component ───────────────────────────────────────────────────────────────

export default function SMRTCOPTemplateEditor(props: Props) {
  const {
    template,
    editingSectionId,
    dirty,
    readOnly,
    mode = "view",
    onStartEdit,
    onCancelEdit,
    onSaveEdit,
    onUpdateSectionLabel,
    onMoveSection,
    onDeleteSection,
    onAddSection,
    onUpdateQuestion,
    onReorderQuestion,
    onAddQuestion,
    onDeleteQuestion,
  } = props;

  const isRun = mode === "run";
  // hideStructure: suppress all structural editing (add/delete/move/rename)
  // true for both read-only views AND run-mode wizards
  const hideStructure = readOnly || isRun;

  // Drag-to-reorder state for template fields (replaces the up/down arrows).
  // A field is dragged by its ⋮ handle; rows are drop targets within the same
  // section. dragOverKey drives the insert-position indicator.
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [dragSectionId, setDragSectionId] = useState<number | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const [dragOverPos, setDragOverPos] = useState<"before" | "after">("before");


  // Section-type chooser shown when user clicks "+ Add Section".
  // Renders as a compact popover below the button (not a full modal).
  const [showAddMenu, setShowAddMenu] = useState(false);
  const addMenuRef = useRef<HTMLDivElement | null>(null);
  const addBtnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!showAddMenu) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (addMenuRef.current?.contains(target) || addBtnRef.current?.contains(target)) return;
      setShowAddMenu(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowAddMenu(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [showAddMenu]);

  // Run-mode answers: keyed by questionKey → user's input value
  const [answers, setAnswers] = useState<Record<string, any>>({});

  const getAnswer = (key: string) => answers[key] ?? "";
  const setAnswer = (key: string, value: any) =>
    setAnswers((prev) => ({ ...prev, [key]: value }));

  // Local state: tracks which questions have helper text row expanded.
  // Key format: "${sectionId}_${questionKey}"
  const [openHelperQuestions, setOpenHelperQuestions] = useState<
    Record<string, boolean>
  >({});

  const toggleHelper = (sectionId: number, questionKey: string) => {
    const key = `${sectionId}_${questionKey}`;
    setOpenHelperQuestions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const isHelperOpen = (sectionId: number, questionKey: string): boolean =>
    !!openHelperQuestions[`${sectionId}_${questionKey}`];

  // Format & Show-When expansion trackers — mirror the Manage Profiles pattern
  // (openFormatFields / openVisibilityFields) but kept COP-local.
  const [openFormatFields, setOpenFormatFields] = useState<Record<string, boolean>>({});
  const [openVisibilityFields, setOpenVisibilityFields] = useState<Record<string, boolean>>({});

  const toggleFormat = (sectionId: number, questionKey: string) => {
    const key = `${sectionId}_${questionKey}`;
    setOpenFormatFields((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const isFormatOpen = (sectionId: number, questionKey: string): boolean =>
    !!openFormatFields[`${sectionId}_${questionKey}`];

  const toggleVisibility = (sectionId: number, questionKey: string) => {
    const key = `${sectionId}_${questionKey}`;
    setOpenVisibilityFields((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const isVisibilityOpen = (sectionId: number, questionKey: string): boolean =>
    !!openVisibilityFields[`${sectionId}_${questionKey}`];

  // ── Read-only field renderer ──────────────────────────────────────────────
  const labelStyle: React.CSSProperties = {
    fontSize: "0.75rem",
    color: "#9aa0a6",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: 2,
  };

  const renderROField = (q: COPQuestion) => {
    const lbl = (
      <div style={labelStyle}>
        {q.label}
        {q.required ? " *" : ""}
      </div>
    );

    switch (q.type) {
      case "heading":
        return (
          <div
            style={{
              fontWeight: 700,
              fontSize: "1rem",
              color: "#ffffff",
              paddingTop: 6,
              borderBottom: "1px solid #2a2a2a",
              paddingBottom: 4,
            }}
          >
            {q.label}
          </div>
        );

      case "textarea":
        return (
          <div>
            {lbl}
            <textarea
              className="manage-input"
              disabled
              placeholder={q.helper || ""}
              style={{
                height: 60,
                padding: "6px 10px",
                resize: "none",
                width: "100%",
                boxSizing: "border-box",
              }}
            />
          </div>
        );

      case "date":
        return (
          <div>
            {lbl}
            <input className="manage-input" type="date" disabled />
          </div>
        );

      case "select":
        return (
          <div>
            {lbl}
            <select className="manage-input" disabled>
              <option>— Select —</option>
              {q.options?.filter(Boolean).map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>
          </div>
        );

      case "checkbox": {
        const opts = q.options?.filter(Boolean).length ? q.options! : ["Yes"];
        return (
          <div>
            {lbl}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 12,
                paddingTop: 4,
              }}
            >
              {opts.map((o) => (
                <label
                  key={o}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    color: "#ccc",
                    fontSize: "0.88rem",
                    cursor: "default",
                  }}
                >
                  <input
                    type="checkbox"
                    disabled
                    style={{ accentColor: "#1faa59" }}
                  />
                  {o}
                </label>
              ))}
            </div>
          </div>
        );
      }

      case "photo":
        return (
          <div>
            {lbl}
            <div
              style={{
                height: 36,
                background: "#1c1c1c",
                border: "1px dashed #2a2a2a",
                borderRadius: 6,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#555",
                fontSize: "0.8rem",
              }}
            >
              📷 Photo upload
            </div>
          </div>
        );

      case "certification":
        return (
          <div
            style={{
              background: "#1a1a1a",
              border: "1px solid #2a2a2a",
              borderRadius: 8,
              padding: 12,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <div style={{ ...labelStyle, color: "#d4a017", marginBottom: 4 }}>
              {q.label}
              {q.required ? " *" : ""} — Certification
            </div>
            <div>
              <div style={labelStyle}>Certified By</div>
              <input
                className="manage-input"
                disabled
                placeholder="Full name"
              />
            </div>
            <div>
              <div style={labelStyle}>Signature</div>
              <input
                className="manage-input"
                disabled
                placeholder="Type full name as digital signature"
              />
            </div>
            <div style={{ fontSize: "0.72rem", color: "#555", marginTop: 2 }}>
              Timestamp: —
            </div>
          </div>
        );

      default: // text, number
        return (
          <div>
            {lbl}
            <input
              className="manage-input"
              type={q.type === "number" ? "number" : "text"}
              disabled
              placeholder={q.helper || ""}
            />
          </div>
        );
    }
  };

  const renderRunField = (q: COPQuestion) => {
    const lbl = (
      <div style={labelStyle}>
        {q.label}
        {q.required ? " *" : ""}
      </div>
    );

    switch (q.type) {
      case "heading":
        return (
          <div
            style={{
              fontWeight: 700,
              fontSize: "1rem",
              color: "#ffffff",
              paddingTop: 6,
              borderBottom: "1px solid #2a2a2a",
              paddingBottom: 4,
            }}
          >
            {q.label}
          </div>
        );

      case "textarea":
        return (
          <div>
            {lbl}
            <textarea
              className="smrtcop-run-input"
              placeholder={q.helper || "Enter text…"}
              value={getAnswer(q.key)}
              onChange={(e) => setAnswer(q.key, e.target.value)}
              style={{
                height: 60,
                padding: "6px 10px",
              }}
            />
          </div>
        );

      case "date":
        return (
          <div>
            {lbl}
            <input
              className="smrtcop-run-input"
              type="date"
              value={getAnswer(q.key)}
              onChange={(e) => setAnswer(q.key, e.target.value)}
            />
          </div>
        );

      case "select":
        return (
          <div>
            {lbl}
            <select
              className="smrtcop-run-input"
              value={getAnswer(q.key)}
              onChange={(e) => setAnswer(q.key, e.target.value)}
            >
              <option value="">— Select —</option>
              {q.options?.filter(Boolean).map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
        );

      case "checkbox": {
        const opts = q.options?.filter(Boolean).length ? q.options! : ["Yes"];
        const checked: string[] = getAnswer(q.key) || [];
        return (
          <div>
            {lbl}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 12,
                paddingTop: 4,
              }}
            >
              {opts.map((o) => (
                <label
                  key={o}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    color: "#ccc",
                    fontSize: "0.88rem",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked.includes(o)}
                    onChange={() => {
                      const next = checked.includes(o)
                        ? checked.filter((v) => v !== o)
                        : [...checked, o];
                      setAnswer(q.key, next);
                    }}
                    style={{ accentColor: "#1faa59" }}
                  />
                  {o}
                </label>
              ))}
            </div>
          </div>
        );
      }

      case "photo":
        return (
          <div>
            {lbl}
            <div
              style={{
                height: 36,
                background: "#1c1c1c",
                border: "1px dashed #1faa59",
                borderRadius: 6,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#1faa59",
                fontSize: "0.8rem",
                cursor: "pointer",
              }}
            >
              📷 Tap to upload photo
            </div>
          </div>
        );

      case "certification":
        return (
          <div
            style={{
              background: "#1a1a1a",
              border: "1px solid #1faa59",
              borderRadius: 8,
              padding: 12,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <div style={{ ...labelStyle, color: "#d4a017", marginBottom: 4 }}>
              {q.label}
              {q.required ? " *" : ""} — Certification
            </div>
            <div>
              <div style={labelStyle}>Certified By</div>
              <input
                className="smrtcop-run-input"
                placeholder="Enter full name"
                value={getAnswer(`${q.key}_certifiedBy`)}
                onChange={(e) =>
                  setAnswer(`${q.key}_certifiedBy`, e.target.value)
                }
              />
            </div>
            <div>
              <div style={labelStyle}>Signature</div>
              <input
                className="smrtcop-run-input"
                placeholder="Type full name as digital signature"
                value={getAnswer(`${q.key}_signature`)}
                onChange={(e) =>
                  setAnswer(`${q.key}_signature`, e.target.value)
                }
              />
            </div>
            <div style={{ fontSize: "0.72rem", color: "#555", marginTop: 2 }}>
              Timestamp:{" "}
              {getAnswer(`${q.key}_signature`)
                ? new Date().toLocaleString()
                : "—"}
            </div>
          </div>
        );

      default: // text, number
        return (
          <div>
            {lbl}
            <input
              className="smrtcop-run-input"
              type={q.type === "number" ? "number" : "text"}
              placeholder={q.helper || "Enter value…"}
              value={getAnswer(q.key)}
              onChange={(e) => setAnswer(q.key, e.target.value)}
            />
          </div>
        );
    }
  };

  return (
    <>
      {!hideStructure && dirty && (
        <div style={{ marginBottom: 16 }}>
          <button className="btn-save" onClick={onSaveEdit}>
            Save Changes
          </button>
        </div>
      )}

      <div className="template-list">
        {template.sections.map((section) => (
          <div
            key={section.id}
            className="template-card"
            style={{ marginBottom: 16 }}
          >
            {/* ── Section header ── */}
            <div className="template-card-header">
              {!hideStructure && editingSectionId === section.id ? (
                <input
                  className="manage-input"
                  value={section.label}
                  onChange={(e) =>
                    onUpdateSectionLabel(section.id, e.target.value)
                  }
                />
              ) : (
                <div className="template-card-title">{section.label}</div>
              )}

              {!hideStructure && (
                <div>
                  {editingSectionId === section.id ? (
                    <>
                      <button className="btn-cancel" onClick={onCancelEdit}>
                        Back
                      </button>
                      {dirty && (
                        <button className="btn-save" onClick={onSaveEdit}>
                          Save
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <button
                        className="btn-move"
                        onClick={() => onMoveSection(section.id, "up")}
                      >
                        ↑
                      </button>
                      <button
                        className="btn-move"
                        onClick={() => onMoveSection(section.id, "down")}
                      >
                        ↓
                      </button>
                      <button
                        className="edit-icon"
                        onClick={() => onStartEdit(section.id)}
                      >
                        ✏️
                      </button>
                      <button
                        className="edit-icon"
                        onClick={() => onDeleteSection(section.id)}
                      >
                        🗑️
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* ── BOM sections: no manual builder, no question list ── */}
            {section.type === "bom" ? (
              <div
                style={{
                  padding: "12px 14px",
                  border: "1px dashed #2a2a2a",
                  borderRadius: 6,
                  color: "#9aa0a6",
                  fontSize: "0.85rem",
                  lineHeight: 1.4,
                }}
              >
                <div style={{ color: "#e6b800", fontWeight: 700, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
                  BOM Section
                </div>
                Auto-populated at runtime from the site's Final BOM file. Crew
                will see Item / Description / Qty / Unit of Measure / and auto
                filled Actual Installed for each line in the BOM.
              </div>
            ) : section.type === "photo" ? (
              /* ── Photo sections: placeholder only; items are derived at
                 runtime from the site's Checklist. Template controls
                 ordering/position but not the item list. ── */
              <div
                style={{
                  padding: "12px 14px",
                  border: "1px dashed #2a2a2a",
                  borderRadius: 6,
                  color: "#9aa0a6",
                  fontSize: "0.85rem",
                  lineHeight: 1.4,
                }}
              >
                <div style={{ color: "#e6b800", fontWeight: 700, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
                  Photo Section
                </div>
                Photo requirements will be auto-populated at runtime from the
                site's checklist file.
              </div>
            ) : section.type === "reports" ? (
              /* ── RET Reports section: sealed uploader (6 TXTRPT slots + RRH
                 serial photos). Template controls ordering/position; the slots
                 themselves are fixed and rendered in the wizard. ── */
              <div
                style={{
                  padding: "12px 14px",
                  border: "1px dashed #2a2a2a",
                  borderRadius: 6,
                  color: "#9aa0a6",
                  fontSize: "0.85rem",
                  lineHeight: 1.4,
                }}
              >
                <div style={{ color: "#e6b800", fontWeight: 700, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
                  RET Reports
                </div>
                Crew uploads the RET scan (.txtrpt) per sector position and the
                Connected-RRH serial photos. Required to advance.
              </div>
            ) : section.type === "alignment" ? (
              /* ── Alignment section: sealed single-slot uploader. Content is
                 fixed; the template only controls its title and position. ── */
              <div
                style={{
                  padding: "12px 14px",
                  border: "1px dashed #2a2a2a",
                  borderRadius: 6,
                  color: "#9aa0a6",
                  fontSize: "0.85rem",
                  lineHeight: 1.4,
                }}
              >
                <div style={{ color: "#e6b800", fontWeight: 700, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
                  Alignment Report
                </div>
                Crew uploads the Alignment Report (PDF or image). Required to
                advance.
              </div>
            ) : section.type === "jha" ? (
              /* ── JHA section: sealed multi-day uploader. Crew uploads one PDF
                 per day onsite (Day 1…, "+" adds more). Content fixed; the
                 template only controls the title and position. ── */
              <div
                style={{
                  padding: "12px 14px",
                  border: "1px dashed #2a2a2a",
                  borderRadius: 6,
                  color: "#9aa0a6",
                  fontSize: "0.85rem",
                  lineHeight: 1.4,
                }}
              >
                <div style={{ color: "#e6b800", fontWeight: 700, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
                  JHA
                </div>
                Crew uploads one JHA PDF per day onsite (Day 1…Day 5 by default,
                "+" adds more). At least Day 1 is required to advance.
              </div>
            ) : (
              <>
                {/* ── Run mode: interactive field widgets ── */}
                {isRun && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {section.questions.map((q) => (
                      <div key={q.key}>{renderRunField(q)}</div>
                    ))}
                  </div>
                )}

                {/* ── Read-only: disabled field widgets ── */}
                {!isRun && readOnly && (
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 12 }}
                  >
                    {section.questions.map((q) => (
                      <div key={q.key}>{renderROField(q)}</div>
                    ))}
                  </div>
                )}

                {/* ── Collapsed: question label preview (edit mode, not selected) ── */}
                {!hideStructure &&
                  editingSectionId !== section.id &&
                  section.questions.map((q) => (
                    <div key={q.key} style={{ color: "#9aa0a6" }}>
                      {q.label}
                      {q.required ? " *" : ""}
                    </div>
                  ))}
              </>
            )}

            {/* ── Expanded: question edit grid (only for manual sections) ── */}
            {!hideStructure &&
              editingSectionId === section.id &&
              section.type !== "bom" &&
              section.type !== "photo" &&
              section.type !== "reports" &&
              section.type !== "alignment" &&
              section.type !== "jha" && (
              <div>
                <div className="template-grid-header">
                  <div className="td-handle-col" />
                  <div>Question</div>
                  <div>Type</div>
                  <div>Options</div>
                  <div>Actions</div>
                </div>

                {section.questions.map((q) => (
                  <React.Fragment key={q.key}>
                    <div
                      className={`template-field-row${
                        dragOverKey === q.key && dragSectionId === section.id
                          ? dragOverPos === "after"
                            ? " td-drag-over-after"
                            : " td-drag-over-before"
                          : ""
                      }`}
                      onDragOver={(e) => {
                        if (
                          dragKey &&
                          dragSectionId === section.id &&
                          dragKey !== q.key
                        ) {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = "move";
                          const rect = e.currentTarget.getBoundingClientRect();
                          const pos =
                            e.clientY > rect.top + rect.height / 2
                              ? "after"
                              : "before";
                          if (dragOverKey !== q.key) setDragOverKey(q.key);
                          if (dragOverPos !== pos) setDragOverPos(pos);
                        }
                      }}
                      onDrop={(e) => {
                        if (
                          dragKey &&
                          dragSectionId === section.id &&
                          dragKey !== q.key
                        ) {
                          e.preventDefault();
                          onReorderQuestion(
                            section.id,
                            dragKey,
                            q.key,
                            dragOverPos === "after",
                          );
                        }
                        setDragKey(null);
                        setDragSectionId(null);
                        setDragOverKey(null);
                      }}
                      onDragLeave={() => {
                        if (dragOverKey === q.key) setDragOverKey(null);
                      }}
                    >
                      {/* Drag handle — grab to reorder this field */}
                      <span
                        className="td-drag-handle"
                        draggable
                        title="Drag to reorder"
                        onDragStart={(e) => {
                          setDragKey(q.key);
                          setDragSectionId(section.id);
                          e.dataTransfer.effectAllowed = "move";
                          e.dataTransfer.setData("text/plain", q.key);
                        }}
                        onDragEnd={() => {
                          setDragKey(null);
                          setDragSectionId(null);
                          setDragOverKey(null);
                        }}
                      >
                        ⋮
                      </span>

                      {/* Label */}
                      <input
                        className="manage-input"
                        value={q.label}
                        onChange={(e) =>
                          onUpdateQuestion(section.id, q.key, {
                            label: e.target.value,
                          })
                        }
                      />

                      {/* Type */}
                      <select
                        className="manage-input"
                        value={q.type}
                        onChange={(e) =>
                          onUpdateQuestion(section.id, q.key, {
                            type: e.target.value as COPQuestionType,
                          })
                        }
                      >
                        <option value="text">Text</option>
                        <option value="textarea">Multi-line</option>
                        <option value="number">Number</option>
                        <option value="date">Date</option>
                        <option value="select">Dropdown</option>
                        <option value="checkbox">Checkbox</option>
                        <option value="photo">Photo</option>
                        <option value="heading">Heading</option>
                        <option value="certification">Certification</option>
                      </select>

                      {/* Options — for select and checkbox only */}
                      {q.type === "select" || q.type === "checkbox" ? (
                        <input
                          className="manage-input"
                          placeholder={
                            q.type === "checkbox"
                              ? "Checkbox items, comma separated"
                              : "Comma separated options"
                          }
                          value={q.options?.join(",") || ""}
                          onChange={(e) =>
                            onUpdateQuestion(section.id, q.key, {
                              options: e.target.value
                                .split(",")
                                .map((o) => o.trim()),
                            })
                          }
                        />
                      ) : (
                        <div />
                      )}

                      {/* Actions */}
                      <div className="template-field-actions">
                        <button
                          className={`btn-toggle ${
                            q.required
                              ? "btn-toggle-mandatory-on"
                              : "btn-toggle-off"
                          }`}
                          title="Required"
                          onClick={() =>
                            onUpdateQuestion(section.id, q.key, {
                              required: !q.required,
                            })
                          }
                        >
                          M
                        </button>
                        <button
                          className="btn-toggle btn-toggle-off"
                          title="Edit User Prompt"
                          onClick={() => toggleHelper(section.id, q.key)}
                        >
                          ✎
                        </button>
                        {/* Format + Show When — manual sections only, no BOM/Photo */}
                        {(!section.type || section.type === "manual") && (q.type === "text" || q.type === "number") && (
                          <button
                            className="btn-toggle btn-toggle-off"
                            title="Format"
                            onClick={() => toggleFormat(section.id, q.key)}
                          >
                            F
                          </button>
                        )}
                        {(!section.type || section.type === "manual") && (
                          <button
                            className="btn-toggle btn-toggle-off"
                            title="Show When"
                            onClick={() => toggleVisibility(section.id, q.key)}
                          >
                            👁
                          </button>
                        )}
                        <button
                          className="btn-toggle btn-toggle-delete"
                          title="Delete"
                          onClick={() => onDeleteQuestion(section.id, q.key)}
                        >
                          🗑
                        </button>
                      </div>
                    </div>

                    {/* Edit User Prompt sub-row */}
                    {isHelperOpen(section.id, q.key) && (
                      <div className="field-prompt-row">
                        <div className="field-prompt-label">Edit User Prompt</div>
                        <input
                          className="manage-input"
                          placeholder="Hint shown to field user"
                          value={q.helper || ""}
                          onChange={(e) =>
                            onUpdateQuestion(section.id, q.key, {
                              helper: e.target.value || undefined,
                            })
                          }
                        />
                      </div>
                    )}

                    {/* Format sub-row — text/number only */}
                    {isFormatOpen(section.id, q.key) && (q.type === "text" || q.type === "number") && (
                      <div className="field-prompt-row">
                        <div className="field-prompt-label">Format</div>
                        <div style={{ display: "flex", gap: 8, flex: 1, flexWrap: "wrap" }}>
                          <input
                            className="manage-input"
                            type="number"
                            placeholder="Min length"
                            style={{ maxWidth: 110 }}
                            value={q.minLength ?? ""}
                            onChange={(e) => {
                              const raw = e.target.value.trim();
                              const n = raw === "" ? undefined : Number(raw);
                              onUpdateQuestion(section.id, q.key, {
                                minLength: Number.isFinite(n as number) ? (n as number) : undefined,
                              });
                            }}
                          />
                          <input
                            className="manage-input"
                            type="number"
                            placeholder="Max length"
                            style={{ maxWidth: 110 }}
                            value={q.maxLength ?? ""}
                            onChange={(e) => {
                              const raw = e.target.value.trim();
                              const n = raw === "" ? undefined : Number(raw);
                              onUpdateQuestion(section.id, q.key, {
                                maxLength: Number.isFinite(n as number) ? (n as number) : undefined,
                              });
                            }}
                          />
                          <input
                            className="manage-input"
                            placeholder="Mask (e.g. *** *** ***)"
                            style={{ flex: 1, minWidth: 180 }}
                            value={q.mask ?? ""}
                            onChange={(e) =>
                              onUpdateQuestion(section.id, q.key, {
                                mask: e.target.value || undefined,
                              })
                            }
                          />
                        </div>
                      </div>
                    )}

                    {/* Show When sub-row */}
                    {isVisibilityOpen(section.id, q.key) && (() => {
                      const rule: COPVisibilityRule = q.visibleWhen?.[0] ?? {
                        questionKey: "",
                        operator: "equals",
                        value: "",
                      };
                      // Candidate controller questions: any select-type question in this template, excluding the current one.
                      const controllers = template.sections
                        .flatMap((s) => s.questions)
                        .filter((other) => other.type === "select" && other.key !== q.key);
                      const controller = controllers.find((c) => c.key === rule.questionKey);

                      const writeRule = (next: Partial<COPVisibilityRule>) => {
                        const merged: COPVisibilityRule = { ...rule, ...next };
                        const hasAny = merged.questionKey || merged.value;
                        onUpdateQuestion(section.id, q.key, {
                          visibleWhen: hasAny ? [merged] : undefined,
                        });
                      };

                      return (
                        <div className="field-prompt-row">
                          <div className="field-prompt-label">Show When</div>
                          <div style={{ display: "flex", gap: 8, flex: 1, flexWrap: "wrap" }}>
                            <select
                              className="manage-input"
                              style={{ maxWidth: 200 }}
                              value={rule.questionKey}
                              onChange={(e) => writeRule({ questionKey: e.target.value, value: "" })}
                            >
                              <option value="">— Select question —</option>
                              {controllers.map((c) => (
                                <option key={c.key} value={c.key}>{c.label || c.key}</option>
                              ))}
                            </select>
                            <select
                              className="manage-input"
                              style={{ maxWidth: 140 }}
                              value={rule.operator}
                              onChange={(e) => writeRule({ operator: e.target.value as COPVisibilityRule["operator"] })}
                            >
                              <option value="equals">equals</option>
                              <option value="notEquals">not equals</option>
                            </select>
                            {controller ? (
                              <select
                                className="manage-input"
                                style={{ flex: 1, minWidth: 160 }}
                                value={rule.value}
                                onChange={(e) => writeRule({ value: e.target.value })}
                                disabled={!rule.questionKey}
                              >
                                <option value="">— Value —</option>
                                {(controller.options || []).filter(Boolean).map((o) => (
                                  <option key={o} value={o}>{o}</option>
                                ))}
                              </select>
                            ) : (
                              <input
                                className="manage-input"
                                placeholder="Value"
                                style={{ flex: 1, minWidth: 160 }}
                                value={rule.value}
                                disabled={!rule.questionKey}
                                onChange={(e) => writeRule({ value: e.target.value })}
                              />
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </React.Fragment>
                ))}

                <div className="template-field-row add-field-row">
                  <div className="add-field-cell">
                    <button
                      className="btn-add btn-add-row"
                      onClick={() => onAddQuestion(section.id)}
                    >
                      + Add Question
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {!hideStructure && (
        <div className="add-step-container" style={{ marginTop: 16 }}>
          <button
            ref={addBtnRef}
            className="btn-add btn-add-step"
            onClick={() => setShowAddMenu(true)}
          >
            + Add Section
          </button>
        </div>
      )}

      {showAddMenu && (
        <div
          className="smrtdocs-modal-overlay"
          onClick={() => setShowAddMenu(false)}
        >
          <div
            ref={addMenuRef}
            className="smrtdocs-modal"
            style={{ width: 340 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>Select Section Type</h2>
              <button className="btn-cancel" onClick={() => setShowAddMenu(false)}>
                Close
              </button>
            </div>
            <div
              className="smrtdocs-modal-body"
              style={{ padding: 0 }}
              role="listbox"
            >
              {([
                { type: "manual", label: "Manual Section" },
                { type: "bom", label: "BOM Section" },
                { type: "photo", label: "Photo Section" },
                { type: "reports", label: "RET Reports" },
                { type: "alignment", label: "Alignment Report" },
                { type: "jha", label: "JHA" },
              ] as const).map((opt) => (
                <div
                  key={opt.type}
                  role="option"
                  aria-selected={false}
                  className="smrtcop-add-section-option"
                  onClick={() => {
                    setShowAddMenu(false);
                    onAddSection(opt.type);
                  }}
                >
                  {opt.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
